// src/components/ContactModal.jsx — "Nous contacter" form modal (homepage, dark theme)
import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, CheckCircle2, AlertTriangle } from 'lucide-react';
import api from '../api/apiClient';

const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

let turnstileScriptPromise = null;
function loadTurnstileScript() {
  if (typeof document === 'undefined') return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  if (!turnstileScriptPromise) {
    turnstileScriptPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${TURNSTILE_SRC}"]`);
      if (existing) {
        existing.addEventListener('load', resolve, { once: true });
        existing.addEventListener('error', reject, { once: true });
        return;
      }
      const script = document.createElement('script');
      script.src = TURNSTILE_SRC;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('turnstile-load'));
      document.head.appendChild(script);
    });
  }
  return turnstileScriptPromise;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const inputClass = (hasError) =>
  `w-full bg-white/5 border rounded-lg px-4 py-2.5 text-sm text-white placeholder:text-slate-500 outline-none transition-colors focus:border-blue-500 focus:ring-1 focus:ring-blue-500/40 ${
    hasError ? 'border-red-500/60' : 'border-white/10'
  }`;

export default function ContactModal({ open, onClose }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [message, setMessage] = useState('');
  const [website, setWebsite] = useState('');
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [statusMessage, setStatusMessage] = useState('');
  const [turnstileReady, setTurnstileReady] = useState(false);

  const nameRef = useRef(null);
  const turnstileBoxRef = useRef(null);
  const turnstileTokenRef = useRef(null);
  const widgetIdRef = useRef(null);

  // Reset form each time the modal opens
  useEffect(() => {
    if (open) {
      setName('');
      setEmail('');
      setCompany('');
      setMessage('');
      setWebsite('');
      setErrors({});
      setStatus('idle');
      setStatusMessage('');
      turnstileTokenRef.current = null;
    }
  }, [open ]);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open ]);

  // Close on Escape
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && status !== 'sending') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, status, onClose ]);

  // Focus first field on open
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => {
      if (nameRef.current) nameRef.current.focus();
    }, 80);
    return () => clearTimeout(t);
  }, [open ]);

  // Render Cloudflare Turnstile widget when open (only if a site key is configured)
  useEffect(() => {
    if (!open || !SITE_KEY) return undefined;
    let cancelled = false;
    const box = turnstileBoxRef.current;
    setTurnstileReady(false);
    loadTurnstileScript()
      .then(() => {
        if (cancelled || !window.turnstile || !box) return;
        try {
          widgetIdRef.current = window.turnstile.render(box, {
            sitekey: SITE_KEY,
            theme: 'dark',
            callback: (token) => {
              turnstileTokenRef.current = token;
            },
            'expired-callback': () => {
              turnstileTokenRef.current = null;
            },
            'error-callback': () => {
              turnstileTokenRef.current = null;
            }
          });
          setTurnstileReady(true);
        } catch (e) {
          turnstileTokenRef.current = null;
        }
      })
      .catch(() => {
        turnstileTokenRef.current = null;
      });
    return () => {
      cancelled = true;
      try {
        if (widgetIdRef.current !== null && window.turnstile) {
          window.turnstile.remove(widgetIdRef.current);
        }
      } catch (e) {
        // ignore cleanup errors
      }
      widgetIdRef.current = null;
      if (box) box.innerHTML = '';
    };
  }, [open ]);

  if (!open) return null;

  const validate = () => {
    const next = {};
    if (!name.trim()) next.name = 'Veuillez indiquer votre nom.';
    if (!email.trim()) next.email = 'Veuillez indiquer votre adresse email.';
    else if (!EMAIL_RE.test(email.trim())) next.email = 'Cette adresse email semble invalide.';
    if (!message.trim()) next.message = 'Veuillez écrire votre message.';
    else if (message.trim().length < 10) next.message = 'Votre message doit contenir au moins 10 caractères.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === 'sending') return;
    if (!validate()) return;

    // Honeypot: pretend everything went fine without calling the API
    if (website) {
      setStatus('success');
      return;
    }

    setStatus('sending');
    setStatusMessage('');
    try {
      await api.post(
        '/contact',
        {
          name: name.trim(),
          email: email.trim(),
          company: company.trim(),
          message: message.trim(),
          turnstileToken: turnstileTokenRef.current
        },
        { silentAuth: true }
      );
      setStatus('success');
    } catch (err) {
      const serverMessage = err && err.data && err.data.message;
      setStatusMessage(
        serverMessage || 'Une erreur est survenue. Veuillez réessayer dans quelques instants.'
      );
      setStatus('error');
    }
  };

  return ReactDOM.createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => status !== 'sending' && onClose()}
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            aria-hidden="true"
          />
          {/* Card */}
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="contact-modal-title"
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-lg rounded-2xl border border-white/10 bg-[#131E3D] shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
          >
            {/* Header */}
            <div className="flex items-start justify-between gap-4 px-6 sm:px-8 pt-6 sm:pt-7">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-blue-400">
                  Contact
                </div>
                <h2 id="contact-modal-title" className="mt-1 text-xl sm:text-2xl font-extrabold tracking-tight text-white">
                  Nous contacter
                </h2>
                <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">
                  Une question sur EBIOSENS ? Écrivez-nous, nous vous répondons sous 48&nbsp;h ouvrées.
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                disabled={status === 'sending'}
                aria-label="Fermer"
                className="p-2 rounded-lg border border-white/10 text-slate-400 hover:text-white hover:bg-white/5 transition-colors shrink-0 disabled:opacity-50"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {status === 'success' ? (
              <div className="px-6 sm:px-8 py-8 flex flex-col items-center text-center">
                <CheckCircle2 className="w-12 h-12 text-emerald-400" />
                <h3 className="mt-4 text-lg font-bold text-white">Message envoyé !</h3>
                <p className="mt-2 text-sm text-slate-300 leading-relaxed">
                  Merci pour votre message. Notre équipe reviendra vers vous très rapidement.
                </p>
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-6 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs uppercase tracking-wider px-6 py-3 rounded-lg shadow-lg shadow-blue-900/30 transition-all"
                >
                  Fermer
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate className="px-6 sm:px-8 py-6 space-y-4">
                {/* Honeypot — invisible to humans */}
                <input
                  type="text"
                  name="website"
                  value={website}
                  onChange={(ev) => setWebsite(ev.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="absolute opacity-0 h-0 w-0 pointer-events-none"
                />

                <div>
                  <label htmlFor="contact-name" className="block text-xs font-bold uppercase tracking-wider text-slate-300 mb-1.5">
                    Nom <span className="text-blue-400">*</span>
                  </label>
                  <input
                    id="contact-name"
                    ref={nameRef}
                    type="text"
                    value={name}
                    onChange={(ev) => setName(ev.target.value)}
                    placeholder="Votre nom"
                    className={inputClass(errors.name)}
                  />
                  {errors.name && <p className="mt-1.5 text-xs text-red-400">{errors.name}</p>}
                </div>

                <div>
                  <label htmlFor="contact-email" className="block text-xs font-bold uppercase tracking-wider text-slate-300 mb-1.5">
                    Email <span className="text-blue-400">*</span>
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    value={email}
                    onChange={(ev) => setEmail(ev.target.value)}
                    placeholder="vous@entreprise.fr"
                    className={inputClass(errors.email)}
                  />
                  {errors.email && <p className="mt-1.5 text-xs text-red-400">{errors.email}</p>}
                </div>

                <div>
                  <label htmlFor="contact-company" className="block text-xs font-bold uppercase tracking-wider text-slate-300 mb-1.5">
                    Société
                  </label>
                  <input
                    id="contact-company"
                    type="text"
                    value={company}
                    onChange={(ev) => setCompany(ev.target.value)}
                    placeholder="Votre société (optionnel)"
                    className={inputClass(false)}
                  />
                </div>

                <div>
                  <label htmlFor="contact-message" className="block text-xs font-bold uppercase tracking-wider text-slate-300 mb-1.5">
                    Message <span className="text-blue-400">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    value={message}
                    onChange={(ev) => setMessage(ev.target.value)}
                    placeholder="Décrivez votre besoin (10 caractères minimum)…"
                    rows={5}
                    className={`${inputClass(errors.message)} resize-y min-h-[120px]`}
                  />
                  {errors.message && <p className="mt-1.5 text-xs text-red-400">{errors.message}</p>}
                </div>

                {/* Turnstile */}
                {SITE_KEY ? (
                  <div className="pt-1">
                    <div ref={turnstileBoxRef} />
                    {!turnstileReady && (
                      <p className="text-xs text-slate-500">Vérification anti-robot en cours de chargement…</p>
                    )}
                  </div>
                ) : (
                  <p className="text-[11px] text-slate-500 italic">
                    Protection anti-robot désactivée (clé Turnstile non configurée — mode développement).
                  </p>
                )}

                {status === 'error' && (
                  <div className="flex items-start gap-2.5 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>{statusMessage}</span>
                  </div>
                )}

                <div className="pt-1 flex flex-col-reverse sm:flex-row sm:justify-end gap-2.5">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={status === 'sending'}
                    className="px-5 py-3 rounded-lg border border-white/15 text-slate-200 font-bold text-xs uppercase tracking-wider hover:bg-white/5 transition-colors disabled:opacity-50"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={status === 'sending'}
                    className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs uppercase tracking-wider px-6 py-3 rounded-lg shadow-lg shadow-blue-900/30 transition-all disabled:opacity-60 disabled:cursor-wait"
                  >
                    <Send className="w-3.5 h-3.5" />
                    {status === 'sending' ? 'Envoi en cours…' : 'Envoyer'}
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
}

ContactModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};
